import { BaseService } from './base/BaseService';
import { Store, StoreCreate, StoreUpdate, ProgramStore } from '../types/entities';
import { API_ENDPOINTS } from '../constants/api';
import { QueryParams } from '../types/common';
import axiosInstance from '../utils/axiosConfig';

export interface StoreFilters {
  chain_id?: number;
  region_id?: number;
  settlement_id?: number;
}

export interface TwoGISStore {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

export interface TwoGISStoreResponse {
  found: number;
  added: number;
  skipped: number;
  stores: TwoGISStore[];
}

export interface AddressFixStats {
  total_stores: number;
  stores_with_settlement: number;
  stores_without_settlement: number;
  percentage_fixed: number;
  examples_needing_fix: Array<{id: number; address: string}>;
}

export interface AddressFixResult {
  message: string;
  processed: number;
  fixed: number;
  errors: string[];
  total_errors: number;
  next_start_id?: number;
}

/**
 * Service for store-related API operations
 */
export class StoreService extends BaseService<Store, StoreCreate, StoreUpdate> {
  constructor() {
    super(API_ENDPOINTS.STORES);
  }

  /**
   * List stores with filters
   */
  async list(params?: QueryParams & { filters?: StoreFilters }): Promise<Store[]> {
    return super.list(params);
  }

  /**
   * List programs for a store
   */
  async listPrograms(storeId: number): Promise<ProgramStore[]> {
    return this.getRequest(`${this.endpoint}/${storeId}/programs`);
  }

  /**
   * Decode address to geoposition
   */
  async decodeAddressGeoposition(address: string): Promise<{ lat: number; lng: number }> {
    return this.postRequest('/api/v1/address-to-geoposition', { address });
  }

  /**
   * Get stores by chain and town from 2GIS
   */
  async getStoresByChainAndTown(
    chainName: string, 
    town: string, 
    addToDb: boolean = false
  ): Promise<TwoGISStoreResponse> {
    return this.postRequest(`${this.endpoint}/2gis-download`, {
      chain_name: chainName,
      town,
      add_to_db: addToDb
    });
  }

  /**
   * Get address fix statistics
   */
  async getAddressFixStats(): Promise<AddressFixStats> {
    return this.getRequest(`${this.endpoint}/address-fix-stats`);
  }

  /**
   * Fix all store addresses
   */
  async fixAllAddresses(): Promise<AddressFixResult> {
    return this.postRequest(`${this.endpoint}/fix-addresses`);
  }

  /**
   * Fix store addresses in batch
   */
  async fixAddressesBatch(batchSize: number = 50, startId: number = 0): Promise<AddressFixResult> {
    return this.postRequest(
      `${this.endpoint}/fix-addresses-batch?batch_size=${batchSize}&start_id=${startId}`
    );
  }
}

// Create singleton instance
export const storeService = new StoreService();

// Export convenience functions for backward compatibility
export const listStores = (filters?: StoreFilters) => 
  storeService.list({ filters });

export const createStore = (payload: StoreCreate) => 
  storeService.create(payload);

export const updateStore = (id: number, payload: StoreUpdate) => 
  storeService.update(id, payload);

export const deleteStore = (id: number) => 
  storeService.delete(id);

export const listProgramsForStore = (storeId: number) => 
  storeService.listPrograms(storeId);

export const listStoresForProgram = async (programId: number): Promise<ProgramStore[]> => {
  const { data } = await axiosInstance.get<ProgramStore[]>(`/api/v1/programs/${programId}/stores`);
  return data;
};

export const decodeAddressGeoposition = (address: string) => 
  storeService.decodeAddressGeoposition(address);

export const getStoresByChainAndTown = (chainName: string, town: string, addToDb?: boolean) => 
  storeService.getStoresByChainAndTown(chainName, town, addToDb);

export const getAddressFixStats = () => 
  storeService.getAddressFixStats();

export const fixAllStoreAddresses = () => 
  storeService.fixAllAddresses();

export const fixStoreAddressesBatch = (batchSize?: number, startId?: number) => 
  storeService.fixAddressesBatch(batchSize, startId);

// Re-export Store interface for backward compatibility
export type { Store } from '../types/entities'; 